import { useState, useEffect } from 'react';
import { Tag, Clock, MapPin, Users, Search, Sparkles, RefreshCw, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { useLocation } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';
import { useFoodlist } from '../contexts/FoodlistContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DealSkeleton = () => (
  <div className="flex gap-0 border rounded-xl overflow-hidden animate-pulse">
    <div className="w-2/5 h-48 bg-gray-200 flex-shrink-0" />
    <div className="flex-1 p-6 space-y-3">
      <div className="h-4 bg-gray-200 rounded w-1/3" />
      <div className="h-5 bg-gray-200 rounded w-3/4" />
      <div className="h-4 bg-gray-200 rounded w-1/2" />
      <div className="h-4 bg-gray-200 rounded w-full" />
      <div className="h-6 bg-gray-200 rounded w-1/4" />
    </div>
  </div>
);

const Deals = () => {
  const { locationString } = useLocation();
  const { user } = useAuth();
  const { items: foodlistItems } = useFoodlist();
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ total: 0, avgSavings: 0, totalSold: 0 });
  const { toast } = useToast();

  const userPreferences = {
    cuisines: [...new Set(foodlistItems.map(i => i.cuisine).filter(Boolean))].slice(0, 3),
  };

  const fetchDeals = async (loc, cat) => {
    if (!loc) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/deals/ai`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          location: loc,
          category: cat || 'all',
          user_preferences: userPreferences,
        }),
      });
      if (!res.ok) throw new Error('Failed to fetch deals');
      const data = await res.json();
      setDeals(data.deals || []);
      setStats(data.stats || { total: data.deals?.length || 0, avgSavings: 35, totalSold: 1800 });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (locationString) fetchDeals(locationString, category);
  }, [locationString]); // eslint-disable-line

  const handleCategoryChange = (val) => {
    setCategory(val);
    if (locationString) fetchDeals(locationString, val);
  };

  const filteredDeals = deals.filter(deal => {
    const q = searchQuery.toLowerCase();
    return !q || deal.title?.toLowerCase().includes(q) || deal.restaurantName?.toLowerCase().includes(q);
  });

  const handleBuyDeal = (deal) => {
    toast({
      title: 'Deal Added!',
      description: `${deal.title} has been added to your cart.`,
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#FF8C42] to-[#F4C430] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2">Food Deals</h1>
              <p className="text-white/90 flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                AI-curated deals {locationString ? `in ${locationString}` : '— set your location to personalise'}
              </p>
            </div>
            {locationString && (
              <Button
                variant="ghost"
                onClick={() => fetchDeals(locationString, category)}
                disabled={loading}
                className="text-white hover:bg-white/20"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* No location */}
        {!locationString && (
          <div className="text-center py-16">
            <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Set your location first</h3>
            <p className="text-gray-500">Use the location prompt to find deals near you</p>
          </div>
        )}

        {locationString && (
          <>
            {/* Search & Filters */}
            <div className="bg-white rounded-xl shadow-sm p-5 mb-8 flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <Input
                  data-testid="deals-search"
                  placeholder="Search deals..."
                  className="pl-10 h-11"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Select value={category} onValueChange={handleCategoryChange}>
                <SelectTrigger className="w-full md:w-52 h-11">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="Food & Dining">Food & Dining</SelectItem>
                  <SelectItem value="Fine Dining">Fine Dining</SelectItem>
                  <SelectItem value="Street Food">Street Food</SelectItem>
                  <SelectItem value="Brunch & Cafe">Brunch & Cafe</SelectItem>
                  <SelectItem value="Cocktail Bar">Cocktail Bar</SelectItem>
                  <SelectItem value="Desserts">Desserts</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: 'Active Deals', value: loading ? '—' : (stats.total || filteredDeals.length) },
                { label: 'Avg Savings', value: `${stats.avgSavings || 35}%` },
                { label: 'Deals Sold', value: `${((stats.totalSold || 1800) / 1000).toFixed(1)}K` },
                { label: 'Avg Rating', value: '4.7' },
              ].map(s => (
                <Card key={s.label}>
                  <CardContent className="p-6 text-center">
                    <div className="text-3xl font-bold text-[#FF6B35] mb-1">{s.value}</div>
                    <div className="text-sm text-gray-600">{s.label}</div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Error */}
            {error && (
              <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 flex items-center gap-2 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4" />{error} —{' '}
                <button onClick={() => fetchDeals(locationString, category)} className="underline">Retry</button>
              </div>
            )}

            {/* Deals Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {loading
                ? Array.from({ length: 6 }).map((_, i) => <DealSkeleton key={i} />)
                : filteredDeals.map((deal, i) => (
                    <Card
                      key={deal.id || i}
                      className="overflow-hidden hover-lift border border-gray-200 hover:border-[#FF6B35] transition-all duration-300"
                      data-testid={`deal-card-${i}`}
                    >
                      <div className="md:flex">
                        <div className="md:w-2/5 relative">
                          <img loading="lazy" src={deal.image} alt={deal.title} className="w-full h-48 md:h-full object-cover" />
                          <Badge className="absolute top-3 left-3 bg-red-500 text-white text-base px-3 py-1">
                            {deal.discount}% OFF
                          </Badge>
                        </div>
                        <CardContent className="md:w-3/5 p-6">
                          <Badge variant="outline" className="mb-3 border-[#FF6B35] text-[#FF6B35]">
                            {deal.category}
                          </Badge>
                          <h3 className="text-xl font-bold text-gray-900 mb-1">{deal.title}</h3>
                          <p className="text-sm font-medium text-gray-700 mb-1">{deal.restaurantName}</p>
                          <p className="text-sm text-gray-600 mb-4 line-clamp-2">{deal.description}</p>
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-2xl font-bold text-[#FF6B35]">${deal.discountPrice}</span>
                            <span className="text-lg text-gray-400 line-through">${deal.originalPrice}</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-600 mb-4">
                            <Users className="w-4 h-4 mr-1 text-[#FF6B35]" />
                            <span>{deal.purchased} purchased</span>
                            <Clock className="w-4 h-4 ml-4 mr-1 text-[#FF6B35]" />
                            <span>Valid until {deal.validUntil ? new Date(deal.validUntil).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'Soon'}</span>
                          </div>
                          <Button
                            className="w-full bg-gradient-to-r from-[#F4C430] via-[#FF8C42] to-[#FF6B35] text-white"
                            onClick={() => handleBuyDeal(deal)}
                          >
                            <Tag className="w-4 h-4 mr-2" />
                            Get This Deal
                          </Button>
                        </CardContent>
                      </div>
                    </Card>
                  ))}
            </div>

            {!loading && filteredDeals.length === 0 && deals.length > 0 && (
              <div className="text-center py-12">
                <Tag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No matching deals</h3>
                <p className="text-gray-500">Try a different search or category</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Deals;
